import React from 'react';
import { cn } from '../../lib/utils';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const MESES_CORTOS = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
];

const DIAS_SEMANA_CORTOS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

/**
 * DateDropdownSelector - Selector de fecha con 3 dropdowns (Día/Mes/Año)
 * 
 * @param {string} value - Fecha en formato 'YYYY-MM-DD' o Date object
 * @param {function} onChange - Callback con la nueva fecha en formato 'YYYY-MM-DD'
 * @param {string} className - Clases CSS adicionales para el contenedor
 * @param {boolean} disabled - Deshabilitar el selector
 * @param {boolean} shortMonths - Usar nombres de meses cortos (Ene, Feb...) - default: true
 * @param {number} yearStart - Año inicial del rango (default: 2020)
 * @param {number} yearEnd - Año final del rango (default: 2030)
 * @param {string} size - Tamaño: 'sm', 'md', 'lg' (default: 'md')
 * @param {boolean} fullWidth - Expandir al ancho completo del contenedor
 */
const DateDropdownSelector = ({
  value,
  onChange,
  className,
  disabled = false,
  shortMonths = true,
  yearStart = 2010,
  yearEnd = 2030,
  size = 'md',
  fullWidth = false,
  id,
  'data-testid': dataTestId
}) => {
  // Parse the value to get day, month, year
  const parseDate = (val) => {
    if (!val) {
      const today = new Date();
      return {
        day: today.getDate(),
        month: today.getMonth(),
        year: today.getFullYear()
      };
    }
    
    let date;
    if (val instanceof Date) {
      date = val;
    } else if (typeof val === 'string') {
      // Handle 'YYYY-MM-DD' format
      const parts = val.split('-');
      if (parts.length === 3) {
        return {
          day: parseInt(parts[2], 10),
          month: parseInt(parts[1], 10) - 1, // 0-indexed
          year: parseInt(parts[0], 10)
        };
      }
      date = new Date(val);
    } else {
      date = new Date();
    }
    
    return {
      day: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear()
    };
  };

  const { day, month, year } = parseDate(value);

  // Get days in the current month
  const getDaysInMonth = (m, y) => {
    return new Date(y, m + 1, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(month, year);

  // Get day of week for a specific date
  const getDayOfWeek = (d, m, y) => {
    const date = new Date(y, m, d);
    return DIAS_SEMANA_CORTOS[date.getDay()];
  };

  // Handle changes
  const handleDayChange = (newDay) => {
    const formattedDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(newDay).padStart(2, '0')}`;
    onChange(formattedDate);
  };

  const handleMonthChange = (newMonth) => {
    // Adjust day if it exceeds days in new month
    const maxDays = getDaysInMonth(newMonth, year);
    const adjustedDay = Math.min(day, maxDays);
    const formattedDate = `${year}-${String(newMonth + 1).padStart(2, '0')}-${String(adjustedDay).padStart(2, '0')}`;
    onChange(formattedDate);
  };

  const handleYearChange = (newYear) => {
    // Adjust day if it exceeds days in new month (for Feb in leap years)
    const maxDays = getDaysInMonth(month, newYear);
    const adjustedDay = Math.min(day, maxDays);
    const formattedDate = `${newYear}-${String(month + 1).padStart(2, '0')}-${String(adjustedDay).padStart(2, '0')}`;
    onChange(formattedDate);
  };

  // Size classes
  const sizeClasses = {
    sm: 'h-8 text-xs px-1',
    md: 'h-9 text-sm px-1.5',
    lg: 'h-10 text-base px-2'
  };

  const selectClass = cn(
    'border bg-white font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none',
    sizeClasses[size]
  );

  const monthNames = shortMonths ? MESES_CORTOS : MESES;

  // Generate year range
  const years = [];
  for (let y = yearStart; y <= yearEnd; y++) {
    years.push(y);
  }

  return (
    <div 
      className={cn(
        'inline-flex items-center',
        fullWidth && 'w-full',
        className
      )}
      data-testid={dataTestId}
      id={id}
    >
      {/* Day selector */}
      <select
        value={day}
        onChange={(e) => handleDayChange(parseInt(e.target.value, 10))}
        disabled={disabled}
        className={cn(
          selectClass, 
          'rounded-l-md border-r-0 min-w-[52px]',
          fullWidth && 'flex-1'
        )}
        data-testid={dataTestId ? `${dataTestId}-day` : undefined}
      >
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => (
          <option key={d} value={d}>
            {String(d).padStart(2, '0')} - {getDayOfWeek(d, month, year)}
          </option>
        ))}
      </select>
      
      {/* Month selector */}
      <select
        value={month}
        onChange={(e) => handleMonthChange(parseInt(e.target.value, 10))}
        disabled={disabled}
        className={cn(
          selectClass, 
          'border-r-0 min-w-[60px]',
          fullWidth && 'flex-[2]'
        )}
        data-testid={dataTestId ? `${dataTestId}-month` : undefined}
      >
        {monthNames.map((mes, idx) => (
          <option key={idx} value={idx}>{mes}</option>
        ))}
      </select>
      
      {/* Year selector */}
      <select
        value={year}
        onChange={(e) => handleYearChange(parseInt(e.target.value, 10))}
        disabled={disabled}
        className={cn(
          selectClass, 
          'rounded-r-md min-w-[65px]',
          fullWidth && 'flex-1'
        )}
        data-testid={dataTestId ? `${dataTestId}-year` : undefined}
      >
        {years.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
};

export { DateDropdownSelector, MESES, MESES_CORTOS };
export default DateDropdownSelector;
